// nuolino-admin/src/app/App.tsx
// Composição raiz: providers + router.

import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Spinner, ToastProvider } from '@/components/ui';
import { AuthProvider, ProtectedRoute, RoleGuard } from '@/auth';
import { AppShell } from '@/components/layout';
import { PatientContextLayout } from '@/components/patient/PatientContextLayout';
import { queryClient } from '@/lib/query-client';

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const NutritionistRegisterPage = lazy(() => import('@/pages/NutritionistRegisterPage'));
const FormulasPage = lazy(() => import('@/pages/FormulasPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const PatientInvitePage = lazy(() => import('@/pages/PatientInvitePage'));
const PatientRegisterPage = lazy(() => import('@/pages/PatientRegisterPage'));
const PatientsListPage = lazy(() => import('@/pages/PatientsListPage'));
const PatientOverviewPage = lazy(() => import('@/pages/PatientOverviewPage'));
const ConsultationsListPage = lazy(() => import('@/pages/ConsultationsListPage'));
const AnamnesisPage = lazy(() => import('@/pages/AnamnesisPage'));
const PatientHealthPage = lazy(() => import('@/pages/PatientHealthPage'));
const GoalsPage = lazy(() => import('@/pages/GoalsPage'));
const ExamsPage = lazy(() => import('@/pages/ExamsPage'));
const AnthropometryPage = lazy(() => import('@/pages/AnthropometryPage'));
const MealPlansPage = lazy(() => import('@/pages/MealPlansPage'));
const PhotosPage = lazy(() => import('@/pages/PhotosPage'));
const ChartPage = lazy(() => import('@/pages/ChartPage'));
const SupplementsPage = lazy(() => import('@/pages/SupplementsPage'));
const CompoundsPage = lazy(() => import('@/pages/CompoundsPage'));
const GuidancePage = lazy(() => import('@/pages/GuidancePage'));
const FilesPage = lazy(() => import('@/pages/FilesPage'));
const ReceiptsPage = lazy(() => import('@/pages/ReceiptsPage'));
const QuestionnairesPage = lazy(() => import('@/pages/QuestionnairesPage'));
const EnergyPage = lazy(() => import('@/pages/EnergyPage'));
const PublicProfilePage = lazy(() => import('@/pages/PublicProfilePage'));
const ProtocolsPage = lazy(() => import('@/pages/ProtocolsPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const AiLogsPage = lazy(() => import('@/pages/AiLogsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'));

function PageFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <Spinner size="lg" />
    </div>
  );
}

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <AppShell>
        <Routes>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="nutritionists/new" element={<NutritionistRegisterPage />} />
          <Route path="formulas" element={<FormulasPage />} />
          <Route path="patients" element={<PatientsListPage />} />
          <Route path="patients/invite" element={<PatientInvitePage />} />
          <Route path="patients/new" element={<PatientRegisterPage />} />
          <Route path="patients/:id" element={<PatientContextLayout />}>
            <Route index element={<PatientOverviewPage />} />
            <Route path="consultations" element={<ConsultationsListPage />} />
            <Route path="anamnesis" element={<AnamnesisPage />} />
            <Route path="health" element={<PatientHealthPage />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="exams" element={<ExamsPage />} />
            <Route path="anthropometry" element={<AnthropometryPage />} />
            <Route path="meal-plan" element={<MealPlansPage />} />
            <Route path="photos" element={<PhotosPage />} />
            <Route path="chart" element={<ChartPage />} />
            <Route path="supplements" element={<SupplementsPage />} />
            <Route path="compounds" element={<CompoundsPage />} />
            <Route path="guidance" element={<GuidancePage />} />
            <Route path="files" element={<FilesPage />} />
            <Route path="receipts" element={<ReceiptsPage />} />
            <Route path="questionnaires" element={<QuestionnairesPage />} />
            <Route path="energy" element={<EnergyPage />} />
          </Route>
          <Route path="consultations" element={<ConsultationsListPage />} />
          <Route path="receipts" element={<ReceiptsPage />} />
          <Route path="public-profile" element={<PublicProfilePage />} />
          <Route path="protocols" element={<ProtocolsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="ai-logs" element={<AiLogsPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="account" element={<AccountPage />} />
        </Routes>
      </AppShell>
    </ProtectedRoute>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<Navigate to="/login" replace />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/forbidden" element={<ForbiddenPage />} />
                <Route
                  path="/*"
                  element={
                    <RoleGuard allow={['nutritionist', 'admin']}>
                      <ProtectedLayout />
                    </RoleGuard>
                  }
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
